import { useEffect, useState } from "react";
import styles from "./css/ChildNotes.module.css";
import { baseURL } from "../../services/axios";
import { showError, showConfirm, showSuccess } from "../../utils/notifications";


interface Note {
  id: number;
  Comment: string;
  AccessPermission: boolean;
  DateTime: string;
  ChildCode: number;
}

export const ChildNotes = ({ childCode, childName }: { childCode: number; childName?: string }) => {
  const [notes, setNotes] = useState<Note[]>([]);
  const [error, setError] = useState<string>("");
  const [showForm, setShowForm] = useState<boolean>(false);
  const [newText, setNewText] = useState<string>("");
  const [accessAllowed, setAccessAllowed] = useState<boolean>(false);

  const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
  const [editedText, setEditedText] = useState<string>("");
  const [editedAccess, setEditedAccess] = useState<boolean>(false);

  useEffect(() => {
    const fetchNotes = async () => {
      try {
const res = await fetch(`${baseURL}/Child/${childCode}/comments`, {
  headers: {
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  },
});

        // const data = await res.json();
        // const filtered = data.filter((c: any) => c.childCode === childCode);
const mapped = (await res.json()).map((c: any) => ({
          id: c.id,
          Comment: c.comments,
          AccessPermission: c.accessPermission,
          DateTime: c.date,
          ChildCode: c.childCode,
        }));
        setNotes(mapped);
      } catch (err) {
        setError("שגיאה בטעינת ההערות");
      }
  };

  fetchNotes();
}, [childCode]);

  const handleAddNote = async () => {
    if (!newText.trim()) return;

    try {
      const response = await fetch(`${baseURL}/Comment`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          comments: newText,
          accessPermission: accessAllowed,
          date: new Date().toISOString(),
          childCode: childCode,
        }),
      });

      if (!response.ok) {
        const text = await response.text();
        setError(`שגיאה בשמירת הערה: ${text}`);
        return;
      }

      const added = await response.json();
      setNotes([
        ...notes,
        {
          id: added.id,
          Comment: added.comments,
          AccessPermission: added.accessPermission,
          DateTime: added.date,
          ChildCode: childCode,
        },
      ]);
      setNewText("");
      setAccessAllowed(false);
      setShowForm(false);
      showSuccess("תוכנית העבודה נוסף בהצלחה! 🎉");
      
    } catch (err) {
      setError("שגיאה בהוספת הערה");
    }
  };

  const handleUpdateNote = async (noteId: number) => {
    try {
      const response = await fetch(`${baseURL}/Comment/${noteId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          id: noteId,
          comments: editedText,
          accessPermission: editedAccess,
        }),
      });

      if (!response.ok) {
        const text = await response.text();
        setError(`שגיאה בעדכון ההערה: ${text}`);
        return;
      }

      const updated = await response.json();
      setNotes((prevNotes) =>
        prevNotes.map((n) =>
          n.id === noteId
            ? {
                ...n,
                Comment: updated.comments,
                AccessPermission: updated.accessPermission,
              }
            : n
        )
      );
      setEditingNoteId(null);
      setEditedText("");
      setEditedAccess(false);
      showSuccess("תוכנית העבודה עודכנה בהצלחה! 🎉");
    } catch (err) {
      setError("שגיאה בעדכון ההערה");
    }
  };

  const handleDeleteNote = async (noteId: number) => {
    if (!window.confirm("האם אתה בטוח שברצונך למחוק תוכנית עבודה זו?")) {
      return;
    }

    try {
      const response = await fetch(`${baseURL}/Comment/${noteId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!response.ok) {
        const text = await response.text();
        setError(`שגיאה במחיקת תוכנית העבודה: ${text}`);
        return;
      }

      setNotes((prevNotes) => prevNotes.filter((note) => note.id !== noteId));
      showSuccess("תוכנית העבודה נמחקה בהצלחה! 🎉");
    } catch (err) {
      setError("שגיאה במחיקת תוכנית העבודה");
    }
  };

  return (
    
    <div 
      className={styles.wrapper} 
      style={{ 
        backgroundColor: 'transparent', 
        background: 'transparent',
        boxShadow: 'none',
        border: 'none'
      }}
    >
                  <div className={styles.betaBanner}>
<p>
לכל מטופל באפשרותך להגדיר אלו תוכניות עבודה יוצגו להורים.
</p>
</div>
      <h2 style={{ 
        textAlign: 'center', 
        fontSize: '1.8rem', 
        marginBottom: '1.5rem',
        color: '#471f1f',
        fontWeight: 600,
        position: 'relative',
        paddingBottom: '12px'
      }}>
        
        תוכניות עבודה {childName ? childName : ''}
        <span style={{
          position: 'absolute',
          bottom: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          width: '80px',
          height: '3px',
          background: 'linear-gradient(to right, #a2b087, #ba9484)',
          borderRadius: '2px',
          display: 'block'
        }}></span>
      </h2>

      {error && <p className={styles.error}>{error}</p>}

      {notes.length === 0 ? (
        <div className={styles.noNotes}>
          <p>לא נמצאו תוכנית עבודה עבור הילד.</p>
          <p style={{ fontSize: '0.9rem', marginTop: '10px', color: '#999' }}>הוסף תוכנית עובדה חדשה באמצעות הכפתור למטה</p>
        </div>
      ) : (
        <>
          <table 
            className={styles.notesTable} 
            style={{ 
              backgroundColor: 'transparent', 
              background: 'transparent',
              boxShadow: 'none'
            }}
          >
            <thead>
              <tr>
                <th style={{ width: '12%' }}>תאריך</th>
                <th style={{ width: '50%' }}>תוכן</th>
                <th style={{ width: '12%' }}>מוצג להורים</th>
                <th style={{ width: '13%' }}>פעולות</th>
                <th style={{ width: '13%' }}>מחיקה</th>
              </tr>
            </thead>
            <tbody>
              {[...notes]
                .sort(
                  (a, b) =>
                    new Date(b.DateTime).getTime() -
                    new Date(a.DateTime).getTime()
                )
                .map((note) =>
                  editingNoteId === note.id ? (
                    <tr key={note.id}>
                      <td colSpan={5} style={{ padding: '15px' }}>
                        <div className={styles.noteForm}>
                          <label style={{ 
                            fontWeight: '600', 
                            fontSize: '1.1rem',
                            color: '#471f1f',
                            marginBottom: '5px'
                          }}>
                            עריכת תוכנית עבודה:
                          </label>
                          <textarea
                            value={editedText}
                            onChange={(e) => setEditedText(e.target.value)}
                            placeholder="כתוב את תוכנית העבודה כאן..."
                            className={styles.textarea}
                          />
                          <button
                            className={`${styles.parentPermissionButton} ${
                              editedAccess ? styles.active : ""
                            }`}
                            onClick={() => setEditedAccess((prev) => !prev)}
                          >
                            {editedAccess
                              ? "✓ התוכנית תוצג להורים (לחץ לביטול)"
                              : "✗ התוכנית לא תוצג להורים (לחץ לאפשר צפייה)"}
                          </button>
                          <div
                            style={{
                              display: "flex",
                              gap: "1rem",
                              marginTop: "1rem",
                              justifyContent: "flex-start"
                            }}
                          >
                            <button
                              className={styles.saveButton}
                              onClick={() => handleUpdateNote(note.id)}
                            >
                              שמור שינויים
                            </button>
                            <button
                              className={styles.cancelButton}
                              onClick={() => setEditingNoteId(null)}
                            >
                              ביטול
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    <tr key={note.id}>
                      <td style={{ fontWeight: '500' }}>{new Date(note.DateTime).toLocaleDateString()}</td>
                      <td className={styles.commentCell}>
                        {note.Comment}
                      </td>
                      <td style={{ textAlign: "center" }}>
                        {note.AccessPermission ? 
                          <span style={{ color: '#4caf50', fontSize: '1.2rem', fontWeight: 'bold' }}>✓</span> : 
                          <span style={{ color: '#f44336', fontSize: '1.2rem', fontWeight: 'bold' }}>✗</span>}
                      </td>
                      <td>
                        <div className={styles.actionButtons}>
                          <button
                            className={styles.editIcon}
                            onClick={() => {
                              setEditingNoteId(note.id);
                              setEditedText(note.Comment);
                              setEditedAccess(note.AccessPermission);
                            }}
                            title="ערוך תוכנית עבודה"
                          >
                            <span className={styles.editIconSymbol}>✏️</span>
                          </button>
                        </div>
                      </td>
                      <td>
                        <button
                          className={styles.deleteIcon}
                          onClick={() => handleDeleteNote(note.id)}
                          title="מחק תוכנית עבודה"
                        >
                          <span className={styles.deleteIconSymbol}>🗑️</span>
                        </button>
                      </td>
                    </tr>
                  )
                )}
            </tbody>
          </table>
        </>
      )}

      <button
        className={styles.newNoteButton}
        onClick={() => setShowForm((prev) => !prev)}
      >
        {showForm ? "ביטול הוספת תוכנית עבודה" : " הוסף תוכנית עבודה חדשה"}
      </button>

      {showForm && (
        <div className={styles.noteForm} style={{ marginTop: '25px' }}>
          <div style={{ 
            marginBottom: '15px', 
            borderBottom: '2px solid #f0f0f0', 
            paddingBottom: '10px'
          }}>
            <h3 style={{ 
              fontSize: '1.3rem', 
              color: '#471f1f',
              fontWeight: '600', 
              margin: '0 0 10px 0' 
            }}>הוספת תוכנית עבודה</h3>
            <p style={{ 
              fontSize: '0.9rem', 
              color: '#777', 
              margin: 0 
            }}>תוכנית עבודה זו תישמר במערכת ותהיה זמינה לצוות המקצועי</p>
          </div>
          
          <label style={{ 
            fontWeight: '600', 
            fontSize: '1.1rem',
            color: '#471f1f',
            marginBottom: '8px',
            display: 'block'
          }}>
            תוכן התוכנית עבודה:
          </label>
          <textarea
            value={newText}
            onChange={(e) => setNewText(e.target.value)}
            placeholder="כתוב את תוכנית העבודה כאן..."
            className={styles.textarea}
          />
          
          <label style={{ 
            fontWeight: '600', 
            fontSize: '1.1rem',
            color: '#471f1f',
            margin: '10px 0 8px',
            display: 'block'
          }}>
            הרשאות צפייה:
          </label>
          <button
            className={`${styles.parentPermissionButton} ${
              accessAllowed ? styles.active : ""
            }`}
            onClick={() => setAccessAllowed((prev) => !prev)}
          >
            {accessAllowed
              ? "✓ התוכנית תוצג להורים (לחץ לביטול)"
              : "✗ התוכנית לא תוצג להורים (לחץ לאפשר צפייה)"}
          </button>
          
          <div style={{ 
            marginTop: '20px', 
            display: 'flex', 
            justifyContent: 'flex-start',
            borderTop: '2px solid #f0f0f0',
            paddingTop: '20px'
          }}>
            <button 
              className={styles.saveButton} 
              onClick={handleAddNote}
              disabled={!newText.trim()}
              style={{ opacity: newText.trim() ? '1' : '0.6' }}
            >
              שמור תוכנית עבודה
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
